import { useState, useEffect, useMemo } from 'react';
import { axiosInstance } from '@/axiosConfig';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plane, CheckCircle, Clock, Search, Play, CheckSquare, ExternalLink, ChevronLeft, ChevronRight, Users, Eye } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Label } from '@/components/ui/label';
import DetalhesVendaModal from '../components/DetalhesVendaModal';

const ITEMS_PER_PAGE = 20;

// --- FUNÇÃO AUXILIAR PARA LOGOS ---
const getAirlineLogo = (nome) => {
  if (!nome) return null;
  const n = nome.toLowerCase().trim();
  
  // URLs públicas (Wikimedia). Para usar locais, substitua por: return '/logos/latam.png';
  if (n.includes('latam')) return 'https://upload.wikimedia.org/wikipedia/commons/f/fe/Latam-logo_-v_%28Indigo%29.svg';
  if (n.includes('gol')) return 'https://upload.wikimedia.org/wikipedia/commons/1/15/GOL-Logo-Pref-FundoClaro-RGB.svg';
  if (n.includes('azul')) return 'https://upload.wikimedia.org/wikipedia/commons/5/52/Logo_da_Azul_Linhas_A%C3%A9reas_Brasileiras.svg';
  if (n.includes('tap')) return 'https://upload.wikimedia.org/wikipedia/commons/a/a3/TAP_Air_Portugal_logo.svg';
  if (n.includes('copa')) return 'https://upload.wikimedia.org/wikipedia/commons/4/43/Copa_Airlines_Logo.svg';
  if (n.includes('american')) return 'https://upload.wikimedia.org/wikipedia/commons/2/23/American_Airlines_logo_2013.svg';
  
  return null;
};

const FlightStatCard = ({ title, value, icon, colorClass }) => (
  <div className="bg-white p-4 rounded-xl border border-gray-200 flex items-center justify-between shadow-sm">
    <div>
      <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">{title}</h3>
      <p className={`text-2xl font-bold mt-1 ${colorClass}`}>{value}</p>
    </div>
    <div className={`p-2 rounded-full ${colorClass.replace('text-', 'bg-').replace('600', '100')} ${colorClass}`}>
      {icon}
    </div>
  </div>
);

export default function GerenciadorVoos() {
  const [voos, setVoos] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState("Todos");
  const [busca, setBusca] = useState("");
  
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVenda, setSelectedVenda] = useState(null);
  const [isModalLoading, setIsModalLoading] = useState(false);

  const [stats, setStats] = useState({
    proximos: 0,
    checkinAberto: 0,
    concluidos: 0,
    total: 0
  });

  const loadVoos = async () => {
    setLoading(true);
    try {
      const params = {};
      if (busca) params.termo_busca = busca;

      const res = await axiosInstance.get('/vendas/voos', { params });
      setVoos(res.data);
      
      const s = { proximos: 0, checkinAberto: 0, concluidos: 0, total: res.data.length };
      res.data.forEach(v => {
          if (v.status_checkin === 'Próximo') s.proximos++;
          if (v.status_checkin === 'Check-in Aberto') s.checkinAberto++;
          if (v.status_checkin === 'Concluído') s.concluidos++;
      });
      setStats(s);

    } catch (error) {
      console.error("Erro ao carregar voos:", error);
      toast.error("Erro ao carregar voos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVoos();
  }, []); 

  const handleSearch = (e) => {
      e.preventDefault();
      loadVoos();
  }

  const filteredVoos = useMemo(() => {
    if (filtroStatus === "Todos") return voos;
    return voos.filter(voo => {
      if (filtroStatus === "Proximos") return voo.status_checkin === 'Próximo';
      if (filtroStatus === "Checkin") return voo.status_checkin === 'Check-in Aberto';
      if (filtroStatus === "Concluidos") return voo.status_checkin === 'Concluído';
      return true;
    });
  }, [voos, filtroStatus]);

  const handleShowDetails = async (vendaId) => {
    if (!vendaId) return;
    setIsModalLoading(true);
    setIsModalOpen(true);
    setSelectedVenda(null);
    try {
      const res = await axiosInstance.get(`/vendas/${vendaId}`);
      setSelectedVenda(res.data);
    } catch (error) {
      toast.error("Erro ao carregar detalhes.");
      setIsModalOpen(false);
    } finally {
      setIsModalLoading(false);
    }
  };

  const getCheckinUrl = (companhia) => {
    if (!companhia) return null;
    const nomeNormalizado = companhia.toLowerCase();
    if (nomeNormalizado.includes('latam')) return 'https://www.latamairlines.com/br/pt/check-in';
    if (nomeNormalizado.includes('gol')) return 'https://b2c.voegol.com.br/check-in/?culture=pt-br';
    if (nomeNormalizado.includes('azul')) return 'https://www.voeazul.com.br/br/pt/home/azulwebcheckin';
    return null;
  };

  const totalPages = Math.ceil(filteredVoos.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, filteredVoos.length);
  
  const currentVoos = useMemo(() => {
      return filteredVoos.slice(startIndex, endIndex);
  }, [filteredVoos, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filtroStatus]);

  const goToPreviousPage = () => { setCurrentPage((prev) => Math.max(prev - 1, 1)); };
  const goToNextPage = () => { setCurrentPage((prev) => Math.min(prev + 1, totalPages)); };

  return (
    // Layout fluido (w-full) e padding reduzido (p-4 md:p-6)
    <div className="flex flex-col w-full p-4 md:p-6 space-y-6">
      
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-1 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          <Plane className="w-7 h-7 text-blue-600" />
          Gerenciador de Voos
        </h1>
        <p className="text-gray-500 text-sm">Visão geral e status de check-in.</p>
      </div>

      {/* Cards de KPIs (Responsivo) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <FlightStatCard title="Próximos" value={stats.proximos} icon={<Clock className="w-4 h-4"/>} colorClass="text-blue-600" />
        <FlightStatCard title="Check-in Aberto" value={stats.checkinAberto} icon={<CheckCircle className="w-4 h-4"/>} colorClass="text-green-600" />
        <FlightStatCard title="Concluídos" value={stats.concluidos} icon={<Play className="w-4 h-4"/>} colorClass="text-orange-600" />
        <FlightStatCard title="Total" value={stats.total} icon={<CheckSquare className="w-4 h-4"/>} colorClass="text-purple-600" />
      </div>

      {/* Filtros Compactos */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="w-full md:w-40">
            <Label className="mb-1 block text-xs uppercase text-gray-500 font-bold">Status</Label>
            <Select value={filtroStatus} onValueChange={setFiltroStatus}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Todos">Todos</SelectItem>
                <SelectItem value="Proximos">Próximos</SelectItem>
                <SelectItem value="Checkin">Check-in Aberto</SelectItem>
                <SelectItem value="Concluidos">Concluídos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 w-full">
             <Label className="mb-1 block text-xs uppercase text-gray-500 font-bold">Buscar</Label>
             <form onSubmit={handleSearch} className="flex gap-2">
                 <Input 
                   placeholder="Nome, Localizador ou Cia..." 
                   value={busca}
                   onChange={(e) => setBusca(e.target.value)}
                   className="flex-1 h-9 text-sm"
                 />
                 <Button type="submit" size="sm" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                     <Search className="w-4 h-4" />
                 </Button>
             </form>
          </div>
        </div>
      </div>

      {/* Tabela Otimizada */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
        <div className="overflow-x-auto w-full">
            <table className="w-full text-left border-collapse">
                <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                        {/* Headers menores e com larguras definidas onde necessário */}
                        <th className="px-3 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider w-[100px]">Cia</th>
                        <th className="px-3 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Passageiros</th>
                        <th className="px-3 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider w-[90px]">Loc.</th>
                        <th className="px-3 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Rota / Data</th>
                        <th className="px-3 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider w-[100px]">Status</th>
                        <th className="px-3 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider w-[120px]">Ações</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {loading ? (
                        <tr><td colSpan="6" className="text-center p-8 text-sm text-gray-500">Carregando voos...</td></tr>
                    ) : filteredVoos.length === 0 ? (
                        <tr><td colSpan="6" className="text-center p-8 text-sm text-gray-500">Nenhum voo encontrado.</td></tr>
                    ) : (
                        currentVoos.map((voo, idx) => {
                            const logoUrl = getAirlineLogo(voo.companhia);
                            const checkinUrl = getCheckinUrl(voo.companhia);
                            
                            let nomes = [];
                            if (voo.cliente_nome) {
                                if (Array.isArray(voo.cliente_nome)) {
                                    nomes = voo.cliente_nome;
                                } else {
                                    nomes = voo.cliente_nome.split(',').map(n => n.trim()).filter(n => n !== "");
                                }
                            }
                            const primeiroPassageiro = nomes[0] || 'Desconhecido';
                            const extraPassengers = nomes.length > 1 ? nomes.length - 1 : 0;

                            return (
                              <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                  {/* Coluna CIA com Logo */}
                                  <td className="px-3 py-3 w-[100px]">
                                    {logoUrl ? (
                                        <img 
                                          src={logoUrl} 
                                          alt={voo.companhia} 
                                          title={voo.companhia}
                                          className="h-6 w-auto object-contain max-w-[80px]" 
                                        />
                                    ) : (
                                        <span className="font-bold text-gray-700 text-xs md:text-sm truncate block max-w-[90px]" title={voo.companhia}>
                                            {voo.companhia}
                                        </span>
                                    )}
                                  </td>
                                  
                                  {/* Coluna Passageiros com quebra de linha */}
                                  <td className="px-3 py-3 max-w-[150px] md:max-w-[220px]">
                                      <div className="flex flex-wrap items-center gap-1">
                                          <span className="font-medium text-gray-900 text-xs md:text-sm break-words leading-tight">
                                            {primeiroPassageiro}
                                          </span>
                                          {extraPassengers > 0 && (
                                              <span 
                                                className="inline-flex items-center justify-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-100 whitespace-nowrap cursor-help" 
                                                title={nomes.slice(1).join(', ')} 
                                              >
                                                  +{extraPassengers} <Users className="w-3 h-3 ml-0.5 opacity-70"/>
                                              </span>
                                          )}
                                      </div>
                                  </td>

                                  {/* Coluna Localizador */}
                                  <td className="px-3 py-3">
                                      <span className="font-mono text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200">
                                          {voo.localizador}
                                      </span>
                                  </td>

                                  {/* Coluna Rota e Data Compactada */}
                                  <td className="px-3 py-3 min-w-[140px]">
                                      <div className="text-xs md:text-sm font-medium text-gray-700 whitespace-normal leading-tight">
                                        {voo.origem} <span className="text-gray-400">➝</span> {voo.destino}
                                      </div>
                                      <div className="text-[11px] text-gray-500 mt-0.5">
                                          {voo.data_partida ? format(parseISO(voo.data_partida), "dd/MM HH:mm") : '-'}
                                      </div>
                                  </td>

                                  {/* Coluna Status */}
                                  <td className="px-3 py-3 text-center">
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] md:text-xs font-semibold border whitespace-nowrap
                                          ${voo.status_checkin === 'Check-in Aberto' ? 'bg-green-100 text-green-800 border-green-200' : 
                                            voo.status_checkin === 'Próximo' ? 'bg-blue-100 text-blue-800 border-blue-200' : 
                                            'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                          {voo.status_checkin === 'Check-in Aberto' ? 'Aberto' : voo.status_checkin}
                                      </span>
                                  </td>

                                  {/* Coluna Ações Compacta */}
                                  <td className="px-3 py-3 text-right">
                                    <div className="flex items-center justify-end gap-1">
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        onClick={() => handleShowDetails(voo.venda_id)}
                                        className="h-7 w-7 text-gray-500 hover:text-blue-600"
                                        title="Ver detalhes"
                                      >
                                        <Eye className="w-3.5 h-3.5" />
                                      </Button>

                                      {checkinUrl ? (
                                          <Button 
                                              size="sm" 
                                              variant="outline" 
                                              className="h-7 text-[11px] px-2 text-blue-600 border-blue-200 hover:bg-blue-50 hover:text-blue-700 whitespace-nowrap"
                                              onClick={() => window.open(checkinUrl, '_blank')}
                                          >
                                              Check-in
                                              <ExternalLink className="w-3 h-3 ml-1" />
                                          </Button>
                                      ) : (
                                          <span className="text-[10px] text-gray-400 italic px-2">Indisp.</span>
                                      )}
                                    </div>
                                  </td>
                              </tr>
                          );
                        })
                    )}
                </tbody>
            </table>
        </div>

        {/* Footer Paginação Compacto */}
        {totalPages > 1 && (
            <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 flex items-center justify-between">
                 <div className="text-xs text-gray-500">
                    <span className="font-medium text-gray-900">{startIndex + 1}</span>-<span className="font-medium text-gray-900">{endIndex}</span> de <span className="font-medium text-gray-900">{filteredVoos.length}</span>
                </div>
                <div className="flex gap-1">
                    <Button onClick={goToPreviousPage} disabled={currentPage === 1} variant="outline" size="sm" className="h-7 w-7 p-0">
                        <ChevronLeft className="h-3 w-3" />
                    </Button>
                    <span className="flex items-center justify-center text-xs font-medium w-8">
                        {currentPage}
                    </span>
                    <Button onClick={goToNextPage} disabled={currentPage === totalPages} variant="outline" size="sm" className="h-7 w-7 p-0">
                        <ChevronRight className="h-3 w-3" />
                    </Button>
                </div>
            </div>
        )}
      </div>

      {selectedVenda && (
        <DetalhesVendaModal
          venda={selectedVenda}
          isOpen={isModalOpen}
          isLoading={isModalLoading}
          onClose={() => { setIsModalOpen(false); setSelectedVenda(null); }}
        />
      )}
    </div>
  );
}